import React from 'react'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import {
  Button,
  ColorCircularProgress,
  ConfigureCardModal,
  ConfirmationModal,
  Icon,
  Typography,
  CardRow,
  Link,
  ErrorModal,
  SplitButton,
  Tooltip,
} from 'components'
import { CONSTANTS, colors } from 'constants/index'
import {
  deleteUserCard,
  getClientPaymentSecret,
  getUserCard,
  getUserCards,
  saveUserCard,
  updateUserCard,
  getBankAccounts,
  updateBankAccount,
  deleteBankAccount,
  setBankAccountDefault,
} from 'store/actions/payments'
import { openSnackbar } from 'store/actions/common'
import Analytics from '../../../utils/analytics/AnalyticsService'
import { EVENTS } from '../../../utils/analytics/Events'
import AddBankAccount from '../../../components/clientAccount/payments/ACHform'
import BankRow from '../../../components/clientAccount/payments/BankRow'
import ClientPaymentApprovalModal from '../../../components/clientAccount/payments/ClientPaymentApprovalModal'

const styles = {
  root: {
    marginBottom: 20,
    paddingRight: '150px',
  },
  buttonIcon: {
    marginRight: 4,
    fontSize: 14,
  },
  title: {
    marginBottom: 8,
  },
  subtitle: {
    marginBottom: 26,
  },
  cards: {
    padding: 25,
    marginBottom: 24,
    border: `1px solid ${colors.BASIC.COLOR[5]}`,
    borderRadius: 4,
    backgroundColor: colors.WHITE,
  },
  manageButton: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    marginLeft: 12,
  },
  manageInnerButtons: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    padding: '5px 10px',
  },
  manageIconButton: {
    minWidth: 'auto',
    padding: '5px 10px 5px 0',
    borderLeft: 'none',
    '&:hover': {
      borderLeft: 'none',
    },
  },
  tooltip: {
    padding: 16,
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
}

class ClientPaymentsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      mode: CONSTANTS.MODE.ADD,
      isModalShown: false,
      isACHModalShown: false,
      isConfirmationModalShown: false,
      currentCardId: '',
      currentBankId: '',
      cardData: {},
      disableBtn: false,
      sortedCards: [],
      selectedBankType: null,
      sortedBanks: [],
      isPaymentModalOpen: false,
      caseId: null,
    }
  }

  componentDidMount() {
    this.props.getCards()
    this.props.getBanks()
    this.checkUrlParams()
  }

  componentDidUpdate(prevProps) {
    if (this.props.userCards !== prevProps.userCards) {
      this.sortingCardsByDefaultProperty()
    }
    if (this.props.bankAccounts !== prevProps.bankAccounts) {
      this.sortingBanksByDefaultProperty()
    }
    if (this.props.location !== prevProps.location) {
      this.checkUrlParams()
    }
  }

  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search)
    const caseIdParam = urlParams.get('case_id')

    if (caseIdParam) {
      this.setState({
        caseId: caseIdParam,
        isPaymentModalOpen: true,
      })
    } else {
      this.setState({
        isPaymentModalOpen: false,
        caseId: null,
      })
    }
  }

  handleClosePaymentModal = () => {
    this.setState({
      isPaymentModalOpen: false,
      caseId: null,
    })
    const url = new URL(window.location)
    url.searchParams.delete('case_id')
    window.history.replaceState({}, '', url)
  }

  sortingCardsByDefaultProperty() {
    const { userCards } = this.props
    const sortedCards = userCards.sort((a, b) => b.isDefault - a.isDefault)
    this.setState({ sortedCards: sortedCards })
  }

  sortingBanksByDefaultProperty() {
    const { bankAccounts } = this.props
    const sortedBanks = bankAccounts.sort((a, b) => b.is_default - a.is_default)
    this.setState({ sortedBanks: sortedBanks })
  }

  onAddNewCardPress() {
    this.props.getSecretKey()
    this.setState({
      isModalShown: true,
      mode: CONSTANTS.MODE.ADD,
      cardData: { label: '', isDefault: false, remember: true },
    })
  }

  async onEditCard(id) {
    try {
      const data = await this.props.getCardDetails(id)
      this.setState({ isModalShown: true, mode: CONSTANTS.MODE.EDIT, cardData: data })
    } catch (e) {
      this.props.showSnackbar('error', 'Card heve not found')
    }
  }

  onModalClose() {
    this.setState({ isModalShown: false })
  }

  onAddBankAccountPress(bankType) {
    this.setState({
      isACHModalShown: true,
      selectedBankType: bankType,
    })
  }

  onACHModalClose() {
    this.setState({
      isACHModalShown: false,
      selectedBankType: null,
    })
  }

  async onModalSave(data) {
    try {
      if (this.state.mode === CONSTANTS.MODE.ADD) {
        await this.props.saveCard(data)
      } else {
        const { uuid, ...payload } = data
        await this.props.updateCard(uuid, payload)
      }
      this.props.getCards()
      this.setState({ isModalShown: false })
    } catch (e) {
      this.setState({ isModalShown: false })
    }
  }

  onDeleteCard(id) {
    this.openConfirmationModalPress(id)
  }

  onConfirmationModalClose() {
    this.setState({ isConfirmationModalShown: false, currentCardId: '', currentBankId: '' })
  }

  async onConfirmationModalConfirm() {
    this.setState({ disableBtn: true })

    try {
      if (this.state.currentCardId) {
        // Delete card
        await this.props.deleteCard(this.state.currentCardId)
        this.setState({ isConfirmationModalShown: false, currentCardId: '' })
        this.props.showSnackbar('success', 'Card deleted')
        const cards = await this.props.getCards()
        Analytics.track(EVENTS.CLIENT_DELETED_CARD, {
          card: !!cards.length,
        })
      } else if (this.state.currentBankId) {
        // Delete bank account
        await this.props.deleteBank(this.state.currentBankId)
        this.setState({ isConfirmationModalShown: false, currentBankId: '' })
        this.props.showSnackbar('success', 'Bank account deleted')
        await this.props.getBanks()
      }
    } catch (e) {
      console.error(e)
    }

    this.setState({ disableBtn: false })
  }

  async onSetDefault(id) {
    const data = {
      is_default: true,
    }
    try {
      await this.props.updateCard(id, data)
      this.props.getCards()
      this.props.getBanks() // Also refresh bank accounts to update their default status
      this.props.showSnackbar('success', 'Card selected as default')
    } catch (e) {
      this.props.showSnackbar('error', 'Error while selecting card as default')
    }
  }

  openConfirmationModalPress(id, type = 'card') {
    if (type === 'bank') {
      this.setState({ isConfirmationModalShown: true, currentBankId: id, currentCardId: '' })
    } else {
      this.setState({ isConfirmationModalShown: true, currentCardId: id, currentBankId: '' })
    }
  }

  async onSetDefaultBank(uuid) {
    try {
      await this.props.setBankDefault(uuid)
      this.props.getBanks()
      this.props.getCards() // Also refresh cards to update their default status
    } catch (e) {
      // Error handling is done in the action
    }
  }

  onDeleteBank(paymentMethodId) {
    this.openConfirmationModalPress(paymentMethodId, 'bank')
  }

  getErrorModalBody = () => {
    return (
      <>
        You can not remove the last payment method. For details please contact{' '}
        <Link href={`mailto:${this.props.adminEmail || 'admin@xirasupport.com'}`}>XIRA Admin</Link>.
      </>
    )
  }
  render() {
    const {
      classes,
      clientSecretIsLoading,
      clientPaymentSecretKey,
      userCards,
      cardsAreLoading,
      bankAccounts,
      banksAreLoading,
    } = this.props
    const {
      isConfirmationModalShown,
      isModalShown,
      isACHModalShown,
      mode,
      cardData,
      sortedCards,
      sortedBanks,
      selectedBankType,
    } = this.state
    return cardsAreLoading ? (
      <ColorCircularProgress />
    ) : (
      <div className={classes.root}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Typography variant="h4" className={classes.title}>
            Payment methods
          </Typography>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Tooltip
              tooltipClass={classes.tooltip}
              title={sortedCards?.length >= 5 ? 'The maximum number of credit cards has been reached.' : ''}
            >
              <Button
                variant="secondary"
                size="small"
                disabled={sortedCards?.length >= 5}
                onClick={() => this.onAddNewCardPress()}
                className={classes.manageButton}
              >
                <Icon style={classes.buttonIcon} /> add credit card
              </Button>
            </Tooltip>
            {sortedBanks?.length >= 5 ? (
              <Tooltip tooltipClass={classes.tooltip} title={'The maximum number of bank accounts has been reached.'}>
                <Button variant="secondary" size="small" disabled={true} className={classes.manageButton}>
                  add bank account
                </Button>
              </Tooltip>
            ) : (
              <SplitButton
                variant="secondary"
                color="secondary"
                size="small"
                onSelect={bankType => this.onAddBankAccountPress(bankType)}
                options={['US bank account', 'Canadian bank account']}
                classes={{
                  buttonGroup: classes.manageButton,
                  buttons: classes.manageInnerButtons,
                  iconButton: classes.manageIconButton,
                }}
              >
                <Icon style={classes.buttonIcon} /> add bank account
              </SplitButton>
            )}
          </div>
        </div>
        {!!(userCards && userCards.length) && (
          <div style={{ marginBottom: 16 }}>
            <Typography variant="subtitle1" style={{ marginBottom: 16, color: colors.BASIC.COLOR[70] }}>
              CARDS
            </Typography>
            <div className={classes.cards}>
              {sortedCards.map(card => (
                <CardRow
                  cardData={card}
                  key={card.uuid}
                  onEditCard={id => this.onEditCard(id)}
                  onSetDefault={id => this.onSetDefault(id)}
                  onDeleteCard={id => this.onDeleteCard(id)}
                  showConfigurationButton
                />
              ))}
            </div>
          </div>
        )}
        {!!(bankAccounts && bankAccounts.length) && (
          <div style={{ marginBottom: 16 }}>
            <Typography variant="subtitle1" style={{ marginBottom: 16, color: colors.BASIC.COLOR[70] }}>
              BANK ACCOUNTS
            </Typography>
            <div className={classes.cards}>
              {sortedBanks.map(bank => (
                <BankRow
                  bankData={bank}
                  key={bank.payment_method_id}
                  onSetDefault={id => this.onSetDefaultBank(id)}
                  onDeleteBank={id => this.onDeleteBank(id)}
                  showConfigurationButton
                />
              ))}
            </div>
          </div>
        )}
        <ConfirmationModal
          title="Remove payment method"
          message={'Are you sure you want to remove this payment method?'}
          confirmationButtonText="Yes, remove"
          isOpen={isConfirmationModalShown && userCards.length + bankAccounts.length !== 1}
          onClose={() => this.onConfirmationModalClose()}
          onConfirm={() => this.onConfirmationModalConfirm()}
          disableButton={this.state.disableBtn}
        />
        <ErrorModal
          title="Remove the last payment method"
          message={this.getErrorModalBody()}
          cancelationButtonText="close"
          isOpen={isConfirmationModalShown && userCards.length + bankAccounts.length === 1}
          onClose={() => this.onConfirmationModalClose()}
        />
        <ConfigureCardModal
          isOpen={isModalShown}
          onClose={() => this.onModalClose()}
          onSave={data => this.onModalSave(data)}
          mode={mode}
          clientPaymentSecretKey={clientPaymentSecretKey}
          clientSecretIsLoading={clientSecretIsLoading}
          cardData={cardData}
        />
        <AddBankAccount isOpen={isACHModalShown} onClose={() => this.onACHModalClose()} bankType={selectedBankType} />
        <ClientPaymentApprovalModal
          isOpen={this.state.isPaymentModalOpen}
          onClose={this.handleClosePaymentModal}
          caseId={this.state.caseId}
        />
      </div>
    )
  }
}

const mapStateToProps = ({ payments }) => ({
  clientSecretIsLoading: payments.clientSecretIsLoading,
  clientPaymentSecretKey: payments.clientPaymentSecretKey,
  userCards: payments.userCards,
  cardsAreLoading: payments.cardsAreLoading,
  bankAccounts: payments.bankAccounts,
  banksAreLoading: payments.bankAccountsAreLoading,
})
const mapDispatchToProps = dispatch => ({
  getSecretKey: () => dispatch(getClientPaymentSecret()),
  getCards: () => dispatch(getUserCards()),
  saveCard: data => dispatch(saveUserCard(data)),
  showSnackbar: (variant, message) => dispatch(openSnackbar(variant, message)),
  updateCard: (uuid, data) => dispatch(updateUserCard(uuid, data)),
  deleteCard: uuid => dispatch(deleteUserCard(uuid)),
  getCardDetails: uuid => dispatch(getUserCard(uuid)),
  getBanks: () => dispatch(getBankAccounts()),
  updateBank: (paymentMethodId, data) => dispatch(updateBankAccount(paymentMethodId, data)),
  deleteBank: paymentMethodId => dispatch(deleteBankAccount(paymentMethodId)),
  setBankDefault: uuid => dispatch(setBankAccountDefault(uuid)),
})

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(ClientPaymentsContainer))
